import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { reportsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { ActionButton, Panel } from '../../components/ui/index.js';
import { RowIndicators } from './ReportsPage.js';
import {
  DeliveryStatusBadge,
  REPORTS_QUERY_KEY,
  ReportNumber,
  ReportStatusBadge,
} from './report-parts.js';

/** One page of a work order's reports; more than this says how many it shows. */
const PAGE_SIZE = 50;

/**
 * The reports of ONE work order, on the work-order file — read-only.
 *
 * It replaced the "not available yet" card, which stopped being true once
 * Phase 10 shipped the report register: a placeholder that is no longer true is
 * a wrong statement about the record.
 *
 * Each report shows its status AND its delivery state, because preparing,
 * issuing and delivering are three separate acts. A caller without
 * `reports.view` sees no section at all — an empty list would assert that no
 * report exists. The "prepare" door is offered to a holder of
 * `reports.prepare`; the server re-checks both on every request.
 */
export function WorkOrderReportsSection({ workOrderId }: { readonly workOrderId: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const mayView = can(PERMISSIONS.REPORTS_VIEW);
  const mayPrepare = can(PERMISSIONS.REPORTS_PREPARE);

  const reports = useQuery({
    queryKey: [...REPORTS_QUERY_KEY, 'register', { workOrderId, pageSize: PAGE_SIZE }],
    queryFn: () => reportsApi.list({ workOrderId, pageSize: PAGE_SIZE }),
    enabled: mayView,
  });

  if (!mayView) return null;

  const rows = reports.data?.data ?? [];
  const total = reports.data?.meta.total ?? rows.length;

  return (
    <Panel
      title={t('reports.workOrderSection.title')}
      description={t('reports.workOrderSection.hint')}
      {...(mayPrepare
        ? {
            actions: (
              <ActionButton
                priority="secondary"
                size="sm"
                onPress={() => navigate(`/reports/new?workOrderId=${encodeURIComponent(workOrderId)}`)}
              >
                {t('reports.workOrderSection.prepare')}
              </ActionButton>
            ),
          }
        : {})}
    >
      {reports.error ? (
        <ErrorNotice error={reports.error} />
      ) : reports.isLoading ? (
        <p className="text-body text-default-600">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="text-body text-default-600">{t('reports.workOrderSection.none')}</p>
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-default-100" data-testid="work-order-reports">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-stack py-2.5 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <Link
                    to={`/reports/${row.id}`}
                    className="measure font-semibold text-primary-700 underline-offset-2 hover:underline"
                    aria-label={`${t('reports.workOrderSection.openReport')} ${row.reportNumber ?? t('reports.numberPending')}`}
                  >
                    <ReportNumber number={row.reportNumber} />
                  </Link>
                  {row.currentVersionNumber !== null ? (
                    <span className="text-caption text-default-600">
                      {t('reports.detail.version', { number: row.currentVersionNumber })}
                    </span>
                  ) : null}
                  <span className="flex flex-wrap items-center gap-inline">
                    <ReportStatusBadge status={row.status} />
                    {row.currentVersionNumber !== null ? (
                      <DeliveryStatusBadge status={row.deliveryStatus} />
                    ) : null}
                  </span>
                  <RowIndicators row={row} />
                </div>
              </li>
            ))}
          </ul>
          {total > rows.length ? (
            <p className="mt-stack text-caption text-default-600">
              {t('workOrders.showingFirst', { shown: rows.length, total })}
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}
